import React from "react";
import { Dispatch } from "redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import { List } from "react-content-loader";
import BreadcrumbItem from "../common/breadcrumbs/BreadcrumbItem";
import MoveTeamWorkflow from "./MoveTeamWorkflow";
import { MetricsState, selectors } from "../logging";
import ErrorBoundary from "../common/ErrorBoundary";
import "./moveTeam.module.css";
import styles from "../common/loader.module.css";
import { RootState } from "../redux/rootReducer";
import bannerSlice, { DisplayBanner } from "../banner/bannerSlice";
import moveTeamFormSchema from "./schema/moveTeamFormSchema";
import { useHttpRequest, LoadingState } from "../http";
import BuildTeamPath from "../common/utils/BuildTeamPath";
import { MoveTeamContext } from "./MoveTeamContext";
import { TeamDto } from "../common/model/team.dto";

interface MoveTeamProps {
  parentTeamId: string;
  teamId: string;
}

interface MoveTeamDispatchProps {
  setBanner: (banner: DisplayBanner | undefined) => void;
}

interface MoveTeamStateProps {
  metrics: MetricsState;
  displayBanner: DisplayBanner | undefined;
}

type MoveTeamPageProps = MoveTeamStateProps &
  MoveTeamDispatchProps &
  RouteComponentProps<MoveTeamProps>;

const MoveTeamPage: React.FC<MoveTeamPageProps> = ({
  metrics,
  match,
  setBanner,
  displayBanner,
}): JSX.Element => {
  // extract props from matched parameters
  const { teamId } = match.params;
  const schema = moveTeamFormSchema;

  // retrieve the breadcrumbs for the current team.
  const {
    loadingState: targetLoadingState,
    data: response,
    error: targetFetchError,
  } = useHttpRequest("moveTeamGetBreadcrumbs", metrics, {
    url: `/api/v1/teams/${teamId}/ancestors?includeCurrentTeam=true`,
    method: "GET",
  });

  if (targetFetchError) {
    if (targetFetchError.response && targetFetchError.response.status === 404) {
      throw new Error(
        `Unable to resolve team with id: ${teamId}.  Redirecting to home page.`
      );
    } else {
      throw targetFetchError;
    }
  }

  const ancestors =
    targetLoadingState === LoadingState.RESOLVED
      ? (response as any).data
      : undefined;

  if (!ancestors) {
    return (
      <div className={styles["loading-holder"]}>
        <List />
      </div>
    );
  }

  const breadcrumbs: BreadcrumbItem[] = ancestors.map(
    (ancestor: { name: string }) => ({ name: ancestor.name })
  );

  const parentTeamName: string = breadcrumbs[breadcrumbs.length - 2].name || "";
  const parentTeamId: string = ancestors[ancestors.length - 2].teamId || "";
  const parentTeamPath = BuildTeamPath(Object.assign([], breadcrumbs), false);
  const currentTeam: TeamDto = ancestors[ancestors.length - 1] || undefined;

  const context: MoveTeamContext = {
    metrics,
    teamId,
    currentTeam,
    parentTeamId,
    parentTeamName,
    parentTeamPath,
    breadcrumbs,
    schema,
    setBanner,
    displayBanner,
  };

  return (
    <ErrorBoundary>
      <MoveTeamWorkflow workflowContext={context} />
    </ErrorBoundary>
  );
};

const mapStateToProperties = (state: RootState): MoveTeamStateProps => ({
  metrics: selectors.getMetrics(state),
  displayBanner: state.banner.displayBanner,
});

const mapDispatchToProperties = (dispatch: Dispatch): MoveTeamDispatchProps => {
  return {
    setBanner: (banner: DisplayBanner | undefined) => {
      dispatch(bannerSlice.actions.setBanner(banner));
    },
  };
};

export default withRouter(
  connect(mapStateToProperties, mapDispatchToProperties)(MoveTeamPage)
);
